import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SignalDetails = ({ signal, isOpen, onClose, onExecuteTrade }) => {
  if (!isOpen || !signal) return null;

  const technicalIndicators = [
    { name: 'RSI', value: signal?.technicalData?.rsi || 65.4, status: 'neutral' },
    { name: 'MACD', value: signal?.technicalData?.macd || 0.0234, status: 'bullish' },
    { name: 'Moving Average', value: signal?.technicalData?.ma || 42150, status: 'bullish' },
    { name: 'Volume', value: signal?.technicalData?.volume || '1.2M', status: 'high' },
    { name: 'Support Level', value: signal?.technicalData?.support || 41800, status: 'strong' },
    { name: 'Resistance Level', value: signal?.technicalData?.resistance || 43200, status: 'moderate' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'bullish': case'strong': case'high':
        return 'text-success';
      case 'bearish': case'weak': case'low':
        return 'text-error';
      case 'neutral': case'moderate':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-primary">
                {signal?.pair?.split('/')?.[0]?.slice(0, 2)}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-card-foreground">{signal?.pair}</h2>
              <p className="text-sm text-muted-foreground">{signal?.exchange}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
          />
        </div>

        <div className="p-6">
          {/* Signal Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Target" size={16} className="text-primary" />
                <span className="text-sm font-medium text-card-foreground">Signal Type</span>
              </div>
              <p className={`text-2xl font-bold ${
                signal?.type === 'buy' ? 'text-success' : 
                signal?.type === 'sell' ? 'text-error' : 'text-warning'
              }`}>
                {signal?.type?.toUpperCase()}
              </p>
              <p className="text-sm text-muted-foreground">Confidence: {signal?.confidence}%</p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="DollarSign" size={16} className="text-primary" />
                <span className="text-sm font-medium text-card-foreground">Price Targets</span>
              </div>
              <p className="text-lg font-semibold text-card-foreground">
                Entry: ${signal?.entryPrice?.toLocaleString()}
              </p>
              <p className="text-sm text-success">Target: ${signal?.targetPrice?.toLocaleString()}</p>
              <p className="text-sm text-error">Stop: ${signal?.stopLoss?.toLocaleString()}</p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Clock" size={16} className="text-primary" />
                <span className="text-sm font-medium text-card-foreground">Timing</span>
              </div>
              <p className="text-sm text-card-foreground">Generated: {signal?.generatedAt}</p>
              <p className="text-sm text-muted-foreground">Expires: {signal?.expiresAt}</p>
              <p className={`text-sm font-medium ${
                signal?.urgency === 'high' ? 'text-error' : 
                signal?.urgency === 'medium' ? 'text-warning' : 'text-muted-foreground'
              }`}>
                Urgency: {signal?.urgency?.toUpperCase()}
              </p>
            </div>
          </div>

          {/* Technical Analysis */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-card-foreground mb-4">Technical Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {technicalIndicators?.map((indicator, index) => (
                <div key={index} className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-card-foreground">{indicator?.name}</span>
                    <span className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(indicator?.status)} bg-current/10`}>
                      {indicator?.status}
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-card-foreground">
                    {typeof indicator?.value === 'number' && indicator?.value > 1000 
                      ? `$${indicator?.value?.toLocaleString()}` 
                      : indicator?.value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* AI Analysis */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-card-foreground mb-4">AI Analysis</h3>
            <div className="bg-muted/50 rounded-lg p-6">
              <p className="text-card-foreground leading-relaxed mb-4">
                {signal?.analysis}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-card-foreground mb-2">Key Factors</h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Strong volume confirmation</li>
                    <li>• Bullish divergence in momentum indicators</li>
                    <li>• Breaking above key resistance level</li>
                    <li>• Positive market sentiment</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-card-foreground mb-2">Risk Factors</h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Overall market volatility</li>
                    <li>• Potential regulatory news impact</li>
                    <li>• Low liquidity during off-hours</li>
                    <li>• External market correlation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Market Context */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-card-foreground mb-4">Market Context</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <h4 className="font-medium text-card-foreground mb-2">Market Sentiment</h4>
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-success h-2 rounded-full" 
                      style={{ width: `${signal?.marketSentiment || 72}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-success">{signal?.marketSentiment || 72}%</span>
                </div>
                <p className="text-xs text-muted-foreground">Bullish sentiment based on social media and news analysis</p>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <h4 className="font-medium text-card-foreground mb-2">Risk Assessment</h4>
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Shield" size={16} className="text-warning" />
                  <span className="text-sm font-medium text-card-foreground">
                    {signal?.riskLevel || 'Medium'} Risk
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Risk-reward ratio: 1:{signal?.riskRewardRatio || 2.5}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
          <Button
            variant={signal?.type === 'buy' ? 'success' : signal?.type === 'sell' ? 'destructive' : 'secondary'}
            onClick={() => onExecuteTrade(signal)}
            iconName={signal?.type === 'buy' ? 'TrendingUp' : signal?.type === 'sell' ? 'TrendingDown' : 'Minus'}
            iconPosition="left"
          >
            Execute {signal?.type?.toUpperCase()} Order
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignalDetails;